<template>
  <AccountDrawer
    v-if="accountDrawerVisibility && accountToShowDrawer"
    v-model:visibility="accountDrawerVisibility"
    :account="accountToShowDrawer" />

  <PeopleDrawer
    v-if="userDetailsDrawerVisibility && userDetailsDrawerUserId"
    v-model:drawer-visibility="userDetailsDrawerVisibility"
    :person-id="userDetailsDrawerUserId"
    @close="userDetailsDrawerUserId = undefined" />

  <!-- Dialogs -->
  <template v-if="optimizationToShow && optimizationToShowPerson">
    <OptimizationDuplicateDialog
      v-if="
        optimizationToShow.type === OptimizationType.SOFTWARE_ACCOUNT_DUPLICATE
      "
      v-model="showOptimizationVisible"
      :optimization="optimizationToShow"
      :person="optimizationToShowPerson"
      @hide="(id: string) => emit('hide', id)"
      @unhide="(id: string) => emit('unhide', id)"
      @open-person-drawer="(id: string) => openUserDetailsDrawer(id)" />

    <OptimizationEmployeeLeavingDialog
      v-else-if="optimizationToShow.type === OptimizationType.EMPLOYEE_LEAVING"
      v-model="showOptimizationVisible"
      :optimization="optimizationToShow"
      :person="optimizationToShowPerson"
      @hide="(id: string) => emit('hide', id)"
      @unhide="(id: string) => emit('unhide', id)"
      @open-person-drawer="(id: string) => openUserDetailsDrawer(id)"
      @open-account-drawer="
        (account: LicenceOut) => openAccountDrawer(account)
      " />

    <OptimizationRedundantLicensesDialog
      v-else-if="
        optimizationToShow.type ===
        OptimizationType.SOFTWARE_ACCOUNT_REDUNDANT_LICENSES
      "
      v-model="showOptimizationVisible"
      :optimization="optimizationToShow"
      :person="optimizationToShowPerson"
      @hide="(id: string) => emit('hide', id)"
      @unhide="(id: string) => emit('unhide', id)"
      @open-person-drawer="(id: string) => openUserDetailsDrawer(id)"
      @open-account-drawer="
        (account: LicenceOut) => openAccountDrawer(account)
      " />

    <OptimizationAccountUnusedDialog
      v-else-if="
        optimizationToShow.type === OptimizationType.SOFTWARE_ACCOUNT_UNUSED
      "
      v-model="showOptimizationVisible"
      :optimization="optimizationToShow"
      :person="optimizationToShowPerson"
      @hide="(id: string) => emit('hide', id)"
      @unhide="(id: string) => emit('unhide', id)"
      @open-person-drawer="(id: string) => openUserDetailsDrawer(id)"
      @open-account-drawer="
        (account: LicenceOut) => openAccountDrawer(account)
      " />

    <OptimizationAccountRightsizingDialog
      v-else-if="
        optimizationToShow.type ===
        OptimizationType.SOFTWARE_ACCOUNT_RIGHTSIZING
      "
      v-model="showOptimizationVisible"
      :optimization="optimizationToShow"
      :person="optimizationToShowPerson"
      @hide="(id: string) => emit('hide', id)"
      @unhide="(id: string) => emit('unhide', id)"
      @open-person-drawer="(id: string) => openUserDetailsDrawer(id)"
      @open-account-drawer="
        (account: LicenceOut) => openAccountDrawer(account)
      " />
  </template>
</template>

<script setup lang="ts">
  import { Account, LicenceOut, OptimizationType } from '@/client'
  import { EnrichedOptimizations } from '@/stores/optimization/optimization.store'
  import { PersonStore } from '@/stores/person/person.store'

  const emit = defineEmits(['hide', 'unhide'])

  // ---------------------------------------------------------------------------
  // Handle Dialogs
  // ---------------------------------------------------------------------------

  // Open Optimization
  const showOptimizationVisible = ref<boolean>(false)
  const optimizationToShow = ref<EnrichedOptimizations>()
  const optimizationToShowPerson = ref<Account>()

  async function showDialog(
    optimization: EnrichedOptimizations,
    personId: string
  ) {
    const person = await PersonStore.getPersonById(personId)

    if (!person) return
    showOptimizationVisible.value = true
    optimizationToShow.value = optimization
    optimizationToShowPerson.value = person
  }

  function closeDialog() {
    showOptimizationVisible.value = false
  }

  watch(
    () => showOptimizationVisible.value,
    (newVal) => {
      if (!newVal) {
        optimizationToShow.value = undefined
        optimizationToShowPerson.value = undefined
      }
    }
  )

  // ---------------------------------------------------------------------------
  // Handle Drawer
  // ---------------------------------------------------------------------------

  const userDetailsDrawerVisibility = ref(false)
  const userDetailsDrawerUserId = ref<string | undefined>()

  function openUserDetailsDrawer(id: string) {
    userDetailsDrawerUserId.value = id
    userDetailsDrawerVisibility.value = true
  }

  const accountDrawerVisibility = ref(false)
  const accountToShowDrawer = ref<LicenceOut | undefined>()

  function openAccountDrawer(account: LicenceOut) {
    accountToShowDrawer.value = account
    accountDrawerVisibility.value = true
    showOptimizationVisible.value = false
  }

  // ---------------------------------------------------------------------------
  // Expose
  // ---------------------------------------------------------------------------

  defineExpose({
    showDialog,
    closeDialog,
  })
</script>
